"use client";

import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { ease, fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";

/**
 * "P/9 Gateway": the sovereign gateway's features, in four numbered chapters.
 *
 * Replaced the "Sovereign compute" note on 2026-08-25. Later the same day the
 * chapters were re-laid: heading, body and points on the left, one large
 * animated panel on the right, for all four, hanging off a numbered rail.
 * The first cut alternated sides and used small static mock-ups that sat
 * shorter than the text beside them; the team called them the worst thing on
 * the page, and they were. The panels now fill the row's height, and each
 * one moves: keys take live requests, a credit meter runs to its cap and
 * stops the key with a 402, spend bars grow and a sparkline draws itself,
 * benchmark bars fill and their numbers count up.
 *
 * The fourth chapter is the benchmark, on the reference layout the team
 * supplied. The figures in `BENCH` are measured: the team supplied the run
 * on 2026-08-26 (`backend-docs/benchmarks/summary.md`, the `i2-pureproxy` rows,
 * produced with the open ai-gateway-reproducible-benchmark harness: 2 vCPUs,
 * 20,000 requests per variant at concurrency 10, median of five trials,
 * against an in-memory mock upstream, with GoModel, Bifrost, Portkey and
 * LiteLLM measured on the same host). They replaced placeholder figures the
 * same day, and the "sub-millisecond" heading went with them: the measured
 * overhead is about two milliseconds at p50. They live in one constant so
 * the next run is one edit. Every other figure in the panels is illustrative
 * traffic, marked `aria-hidden`.
 *
 * Monochrome throughout: where the reference leads each heading with a
 * coloured phrase, here that phrase carries weight rather than hue.
 */

interface Chapter {
  n: string;
  lead: string;
  rest: string;
  body: string;
  points: Array<{ strong: string; rest: string; href?: string; linkLabel?: string }>;
  art: "keys" | "policy" | "spend" | "bench";
}

/** The harness the run was made with; the method and the mock live there. */
const BENCH_URL = "https://github.com/ENTERPILOT/ai-gateway-reproducible-benchmark";

const CHAPTERS: Chapter[] = [
  {
    n: "01",
    lead: "Access:",
    rest: "give your whole team every model.",
    body: "Put every route P/9 serves behind one OpenAI-compatible API and one organisation login. Keys are minted per project and scoped to the models you choose, and the alias you call stays stable when the weights behind it move, so platform teams open access without becoming the bottleneck.",
    points: [
      { strong: "One OpenAI-compatible API", rest: "to every route. Change the model string, not your stack." },
      { strong: "Keys per project,", rest: "scoped by allowed models, with an expiry and a rupee credit limit." },
      { strong: "PK NPU-native and global pass-through routes", rest: "through the same gateway and the same keys." },
      { strong: "New open models arrive as routes,", rest: "not as a code change on your side." },
    ],
    art: "keys",
  },
  {
    n: "02",
    lead: "Visibility and control:",
    rest: "see every request, and cap it before it runs.",
    body: "Usage and spend are attributed to every key, project and model, so chargeback is a filter rather than a spreadsheet. Give a key a hard credit limit and it stops at the cap with a 402 while the rest of your balance stays intact. Telemetry, the audit log and the ledger are kept in Pakistan.",
    points: [
      { strong: "Requests, tokens, time to first token and throughput", rest: "per key, per project and per model." },
      { strong: "Hard credit limits per key:", rest: "at the cap that key stops, and nothing else does." },
      { strong: "Expiry and instant revocation,", rest: "honoured by the gateway on the very next request." },
      { strong: "Model access control per key,", rest: "with a 403 for anything outside scope." },
      { strong: "An append-only audit log", rest: "of every administrative change, exportable as CSV." },
    ],
    art: "policy",
  },
  {
    n: "03",
    lead: "Cost:",
    rest: "priced in rupees, end to end.",
    body: "Rates are quoted per million tokens in PKR, debited from a prepaid balance and settled in the same currency. No foreign card, no exchange-rate surprise at month end, and the exact cost of every request reported back by the gateway.",
    points: [
      { strong: "Per-million-token prices in rupees,", rest: "visible before you call." },
      { strong: "A prepaid balance:", rest: "budget exhaustion fails closed, never on credit." },
      { strong: "Per-key spend", rest: "for chargeback across teams and products." },
      { strong: "The cost of each request", rest: "returned in the response's debug metrics." },
    ],
    art: "spend",
  },
  {
    n: "04",
    lead: "Performance:",
    rest: "two milliseconds of overhead. Read the benchmark.",
    body: "The gateway is a Go service on the request path: it authenticates the key, checks scope and budget, meters the tokens and relays. Its overhead is measured with an open, reproducible harness against a deterministic mock upstream, beside four other gateways on the same host, so the number is the gateway's alone and the comparison is a fair one.",
    points: [
      { strong: "About 2 ms added at p50 and under 7 ms at p99,", rest: "key, scope and budget checks included." },
      { strong: "4,520 requests a second", rest: "sustained from two vCPUs, ahead of every gateway measured beside it." },
      { strong: "A 3.4 MB image and under 50 MB of memory,", rest: "so it runs beside the model rather than on a fleet of its own." },
      { strong: "Run it yourself:", rest: "the harness is open source, one command on a fresh box.", href: BENCH_URL, linkLabel: "See the harness" },
    ],
    art: "bench",
  },
];

interface Tile {
  value: number;
  unit: string;
  label: string;
  decimals?: number;
  prefix?: string;
  suffix?: string;
}

const TILES: Tile[] = [
  { value: 1.99, decimals: 2, unit: "ms", label: "p50 overhead added" },
  { value: 6.75, decimals: 2, unit: "ms", label: "p99 through the gateway" },
  { value: 4520, unit: "req/s", label: "peak sustained, 2 vCPUs" },
  { value: 26.7, decimals: 1, unit: "MB", label: "memory under load" },
];

/**
 * Measured figures, from `backend-docs/benchmarks/summary.md` (run supplied
 * 2026-08-26). Update every value here, and only here, when a new run is
 * recorded. Shape follows the reference: four tiles, then two bar charts
 * with the run's conditions under each. The latency rows are P/9's own, chat
 * completions non-streaming; the throughput rows are the peak of the
 * concurrency sweep for every gateway in the run, P/9 first.
 */
const BENCH = {
  tiles: TILES,
  overhead: {
    title: "Latency through the gateway, by percentile (ms, lower is better)",
    rows: [
      { label: "p50", value: 2.28 },
      { label: "p90", value: 4.14 },
      { label: "p99", value: 6.75, strong: true },
    ],
    max: 8,
    decimals: 2,
    unit: "ms",
    note: "Chat completions, non-streaming: 20,000 requests at concurrency 10, median of five trials. Direct to the same mock upstream: 0.29 ms p50, 2.51 ms p99.",
  },
  throughput: {
    title: "Sustained requests per second, one instance of each gateway (higher is better)",
    rows: [
      { label: "P/9", value: 4520, strong: true },
      { label: "GoModel", value: 3961 },
      { label: "Bifrost", value: 2560 },
      { label: "Portkey", value: 993 },
      { label: "LiteLLM", value: 288 },
    ],
    max: 5000,
    decimals: 0,
    unit: "req/s",
    note: "Same 2 vCPU host and mock upstream, retries and per-request logging off everywhere; the peak of a concurrency sweep from 1 to 256.",
  },
};

/* ------------------------------------------------------------- helpers */

/** Thousands separators without `toLocaleString`, which the SSR rules forbid. */
function fmt(n: number, decimals = 0): string {
  const fixed = n.toFixed(decimals);
  const [int, frac] = fixed.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac ? `${grouped}.${frac}` : grouped;
}

/** A number that counts from zero to `to` once `go` is true. */
function Count({
  to,
  go,
  decimals = 0,
  prefix = "",
  suffix = "",
  duration = 1.6,
  reduced,
}: {
  to: number;
  go: boolean;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  reduced: boolean;
}) {
  const mv = useMotionValue(0);
  const text = useTransform(mv, (v) => `${prefix}${fmt(v, decimals)}${suffix}`);
  useEffect(() => {
    if (!go || reduced) return;
    const c = animate(mv, to, { duration, ease });
    return () => c.stop();
  }, [go, to, mv, duration, reduced]);
  if (reduced) return <>{`${prefix}${fmt(to, decimals)}${suffix}`}</>;
  return <motion.span>{text}</motion.span>;
}

/** A horizontal bar that fills to `pct` once `go` is true. */
function Fill({ pct, go, delay = 0, strong = true }: { pct: number; go: boolean; delay?: number; strong?: boolean }) {
  return (
    <span className="block h-2 w-full overflow-hidden rounded-full bg-foreground/[0.08]">
      <motion.span
        className={cn("block h-full rounded-full", strong ? "bg-foreground/80" : "bg-foreground/35")}
        initial={{ width: "0%" }}
        animate={{ width: go ? `${pct}%` : "0%" }}
        transition={{ duration: 1.1, ease, delay }}
      />
    </span>
  );
}

/** Ticks up every `ms` while `run`; the panels use it to rotate their state. */
function useTicker(run: boolean, ms: number): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!run) return;
    const id = window.setInterval(() => setTick((t) => t + 1), ms);
    return () => window.clearInterval(id);
  }, [run, ms]);
  return tick;
}

const PANEL =
  "flex h-full min-h-[24rem] flex-col rounded-lg border border-border bg-card p-5 shadow-[0_32px_80px_-48px_rgba(0,0,0,0.45)] sm:p-7 lg:min-h-[30rem]";
const PILL = "rounded-full border border-border px-2 py-0.5 font-sans text-[0.62rem] uppercase tracking-[0.12em] text-muted";
const MONO = "font-mono text-[0.7rem] text-muted";
const LABEL = "font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2";
const HEAD = "font-sans text-sm font-medium text-foreground";

interface ArtProps {
  go: boolean;
  reduced: boolean;
}

/* -------------------------------------------------------------- 01 keys */

const KEY_ROWS = [
  { key: "chatbot-prod", scope: "gpt-oss 120B, Kimi K3", spend: 4182, cap: 10000 },
  { key: "rag-indexer", scope: "qwen3-embedding", spend: 612, cap: 2000 },
  { key: "ci-eval", scope: "Every model", spend: 58, cap: 250 },
  { key: "support-agent", scope: "Kimi K3", spend: 1440, cap: 5000 },
];

const REQUESTS = [
  { route: "chat/completions", model: "gpt-oss-120b(Global)", tokens: "1,204 tok", cost: "Rs 0.62" },
  { route: "embeddings", model: "qwen3-embedding", tokens: "3,410 tok", cost: "Rs 0.11" },
  { route: "chat/completions", model: "Kimi-K3(Global)", tokens: "842 tok", cost: "Rs 0.58" },
  { route: "chat/completions", model: "Kimi-K3(Global)", tokens: "2,010 tok", cost: "Rs 1.36" },
];

function KeysArt({ go, reduced }: ArtProps) {
  const tick = useTicker(go && !reduced, 2600);
  const active = tick % KEY_ROWS.length;
  const req = REQUESTS[active];
  return (
    <div className={PANEL} aria-hidden="true">
      <div className="flex items-center justify-between">
        <span className={HEAD}>API keys</span>
        <motion.span
          className="rounded-md bg-foreground px-2.5 py-1 font-sans text-[0.66rem] font-medium text-background"
          animate={reduced ? undefined : { scale: [1, 1.04, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        >
          Create API key
        </motion.span>
      </div>

      <div className="mt-5 grid grid-cols-[1.1fr_1.2fr_1fr] gap-3 border-b border-border pb-2">
        <span className={LABEL}>Key</span>
        <span className={LABEL}>Scope</span>
        <span className={cn(LABEL, "text-right")}>Spend / cap</span>
      </div>

      <ul className="flex flex-col">
        {KEY_ROWS.map((r, i) => {
          const on = i === active;
          const pct = Math.min(100, (r.spend / r.cap) * 100 + (on ? 4 : 0));
          return (
            <li key={r.key} className="relative border-b border-border last:border-b-0">
              <div className="relative grid grid-cols-[1.1fr_1.2fr_1fr] items-center gap-3 px-2 py-3">
                <motion.span
                  className="absolute inset-0 rounded-md bg-foreground/[0.05]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: on && !reduced ? 1 : 0 }}
                  transition={{ duration: 0.5 }}
                />
                <span className="relative min-w-0">
                  <span className="block truncate font-sans text-sm text-foreground">{r.key}</span>
                  <span className={cn(MONO, "block")}>p9_live_…</span>
                </span>
                <span className="relative truncate font-sans text-xs text-muted">{r.scope}</span>
                <span className="relative text-right">
                  <span className="block font-sans text-sm tabular-nums text-foreground">
                    Rs <Count to={r.spend} go={go} reduced={reduced} duration={1.2} />
                    <span className="text-muted-2"> / {fmt(r.cap)}</span>
                  </span>
                  <span className="mt-1.5 block">
                    <Fill pct={pct} go={go} delay={0.15 * i} />
                  </span>
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-auto pt-5">
        <p className={LABEL}>Live</p>
        <div className="relative mt-2 h-10">
          <AnimatePresence initial={false}>
            <motion.div
              key={reduced ? "static" : tick}
              initial={reduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease }}
              className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  {!reduced && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-foreground/60" />}
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-foreground" />
                </span>
                <span className={cn(MONO, "truncate text-foreground")}>
                  POST /v1/{req.route} · {KEY_ROWS[active].key}
                </span>
              </span>
              <span className={cn(MONO, "shrink-0 tabular-nums")}>
                200 · {req.tokens} · {req.cost}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ 02 policy */

const CAP = 10000;
const LOOP = 7.5;
const AUDIT = [
  { event: "key.limit_updated", detail: "chatbot-prod, Rs 10,000", when: "2 min ago" },
  { event: "key.models_updated", detail: "chatbot-prod, 2 allowed", when: "2 min ago" },
  { event: "key.created", detail: "support-agent, expires in 90 days", when: "yesterday" },
];

function PolicyArt({ go, reduced }: ArtProps) {
  const spent = useMotionValue(0);
  const spentText = useTransform(spent, (v) => `Rs ${fmt(v)}`);
  useEffect(() => {
    if (!go || reduced) return;
    const c = animate(spent, [0, CAP, CAP], {
      duration: LOOP,
      times: [0, 0.74, 1],
      ease: ["easeInOut", "linear"],
      repeat: Infinity,
      repeatType: "loop",
    });
    return () => c.stop();
  }, [go, reduced, spent]);

  const field = "rounded-md border border-border bg-background px-3 py-2 font-sans text-sm text-foreground";
  const loop = { duration: LOOP, repeat: Infinity, repeatType: "loop" as const };

  return (
    <div className={PANEL} aria-hidden="true">
      <div className="flex items-center justify-between gap-3">
        <span className={cn(HEAD, "truncate")}>chatbot-prod · limits and scope</span>
        <span className={PILL}>Production</span>
      </div>

      <dl className="mt-6 grid grid-cols-[6.5rem_1fr] items-center gap-x-4 gap-y-3.5">
        <dt className="font-sans text-xs text-muted">Credit limit</dt>
        <dd className={field}>
          Rs 10,000 <span className="text-muted-2">· lifetime</span>
        </dd>
        <dt className="font-sans text-xs text-muted">Expires</dt>
        <dd className={field}>90 days</dd>
        <dt className="font-sans text-xs text-muted">Allowed models</dt>
        <dd className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-foreground px-2.5 py-1 font-mono text-[0.66rem] text-background">gpt-oss-120b(Global)</span>
          <span className="rounded-full bg-foreground px-2.5 py-1 font-mono text-[0.66rem] text-background">Kimi-K3(Global)</span>
          <motion.span
            className="rounded-full border border-border px-2.5 py-1 font-mono text-[0.66rem] text-muted"
            animate={reduced ? undefined : { opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          >
            qwen3-embedding
          </motion.span>
        </dd>
      </dl>

      <div className="mt-7 rounded-lg border border-border bg-background p-4">
        <div className="flex items-baseline justify-between gap-3">
          <span className={LABEL}>Spent against the cap</span>
          <span className="font-sans text-sm tabular-nums text-foreground">
            {reduced ? "Rs 10,000" : <motion.span>{spentText}</motion.span>}
            <span className="text-muted-2"> / Rs 10,000</span>
          </span>
        </div>
        <span className="mt-3 block h-2.5 w-full overflow-hidden rounded-full bg-foreground/[0.08]">
          <motion.span
            className="block h-full rounded-full bg-foreground/80"
            initial={{ width: reduced ? "100%" : "0%" }}
            animate={go && !reduced ? { width: ["0%", "100%", "100%"] } : undefined}
            transition={{ ...loop, times: [0, 0.74, 1], ease: ["easeInOut", "linear"] }}
          />
        </span>
        <div className="mt-3 flex min-h-[1.5rem] items-center justify-between gap-3">
          <motion.span
            className="inline-flex items-center gap-2 rounded-md border border-border-strong bg-card px-2.5 py-1 font-mono text-[0.68rem] text-foreground"
            initial={{ opacity: reduced ? 1 : 0 }}
            animate={go && !reduced ? { opacity: [0, 0, 1, 1] } : undefined}
            transition={{ ...loop, times: [0, 0.75, 0.8, 1] }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
            402 · cap reached, this key only
          </motion.span>
          <span className="font-sans text-xs text-muted">The balance stays intact.</span>
        </div>
      </div>

      <div className="mt-6">
        <p className={LABEL}>Audit log</p>
        <ul className="mt-2 divide-y divide-border border-y border-border">
          {AUDIT.map((a, i) => (
            <motion.li
              key={a.event}
              className="grid grid-cols-[1fr_auto] items-center gap-3 py-2"
              initial={{ opacity: 0, x: -6 }}
              animate={go ? { opacity: 1, x: 0 } : undefined}
              transition={{ duration: 0.5, ease, delay: 0.3 + i * 0.12 }}
            >
              <span className="min-w-0 truncate">
                <span className="font-mono text-[0.7rem] text-foreground">{a.event}</span>
                <span className="font-sans text-xs text-muted"> · {a.detail}</span>
              </span>
              <span className={cn(MONO, "shrink-0 tabular-nums")}>{a.when}</span>
            </motion.li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex items-center justify-end gap-3 pt-6">
        <span className="font-sans text-xs text-muted">Revoke</span>
        <span className="rounded-md bg-foreground px-3 py-1.5 font-sans text-[0.66rem] font-medium text-background">Save</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- 03 spend */

const SPEND_KEYS = [
  { label: "chatbot-prod", value: 4182.55, pct: 100 },
  { label: "support-agent", value: 1440.2, pct: 34 },
  { label: "rag-indexer", value: 612.1, pct: 15 },
  { label: "ci-eval", value: 58.44, pct: 3 },
];
const SPEND_MODELS = [
  { label: "gpt-oss-120b(Global)", value: 3120.4, pct: 100 },
  { label: "Kimi-K3(Global)", value: 2560.79, pct: 82 },
  { label: "qwen3-embedding", value: 612.1, pct: 20 },
];
const TOTAL = 6293.29;

/**
 * Thirty days of spend for the sparkline: a rising trend with seeded noise
 * on it, so the line reads as a real month rather than a sawtooth, and the
 * same month on the server and on the client (the seed is fixed; nothing here
 * touches Math.random). Drawn as a Catmull-Rom spline through the points.
 */
function seeded(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SPARK_POINTS: [number, number][] = (() => {
  const rnd = seeded(20260826);
  const days = 30;
  const out: [number, number][] = [];
  let drift = 0;
  for (let i = 0; i < days; i++) {
    drift = drift * 0.72 + (rnd() - 0.5) * 0.26;
    const trend = 0.18 + (i / (days - 1)) * 0.62;
    const week = Math.sin((i / 7) * Math.PI * 2) * 0.07;
    const v = Math.min(1, Math.max(0.05, trend + week + drift));
    out.push([(i / (days - 1)) * 120, 36 - v * 31]);
  }
  return out;
})();

function spline(points: [number, number][]): string {
  if (points.length < 2) return "";
  let d = `M${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(2)} ${c1y.toFixed(2)} ${c2x.toFixed(2)} ${c2y.toFixed(2)} ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return d;
}

const SPARK = spline(SPARK_POINTS);
const SPARK_END = SPARK_POINTS[SPARK_POINTS.length - 1];

function SpendArt({ go, reduced }: ArtProps) {
  return (
    <div className={PANEL} aria-hidden="true">
      <div className="flex items-baseline justify-between gap-3">
        <span className={HEAD}>Spend · last 30 days</span>
        <span className="font-sans text-lg tabular-nums text-foreground">
          Rs <Count to={TOTAL} go={go} decimals={2} reduced={reduced} duration={1.8} />
        </span>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-background px-4 pb-3 pt-4">
        <div className="flex items-center justify-between">
          <span className={LABEL}>Daily</span>
          <span className={MONO}>PKR, debited from the prepaid balance</span>
        </div>
        <svg viewBox="0 0 120 40" className="mt-3 h-auto w-full" role="presentation">
          {[10, 20, 30].map((y) => (
            <line key={y} x1={0} x2={120} y1={y} y2={y} stroke="var(--border)" strokeWidth={0.4} />
          ))}
          <motion.path
            d={`${SPARK} L120 40 L0 40 Z`}
            fill="var(--foreground)"
            initial={{ opacity: reduced ? 0.06 : 0 }}
            animate={go && !reduced ? { opacity: [0, 0, 0.06, 0.06] } : undefined}
            transition={{ duration: 6, times: [0, 0.42, 0.55, 1], repeat: Infinity, repeatType: "loop" }}
          />
          <motion.path
            d={SPARK}
            fill="none"
            stroke="var(--foreground)"
            strokeWidth={0.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: reduced ? 1 : 0 }}
            animate={go && !reduced ? { pathLength: [0, 1, 1] } : undefined}
            transition={{ duration: 6, times: [0, 0.45, 1], repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
          />
          <motion.circle
            cx={SPARK_END[0]}
            cy={SPARK_END[1]}
            r={1.6}
            fill="var(--foreground)"
            initial={{ opacity: reduced ? 1 : 0 }}
            animate={go && !reduced ? { opacity: [0, 0, 1, 1] } : undefined}
            transition={{ duration: 6, times: [0, 0.44, 0.5, 1], repeat: Infinity, repeatType: "loop" }}
          />
        </svg>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {[
          ["By key", SPEND_KEYS],
          ["By model", SPEND_MODELS],
        ].map(([title, rows], gi) => (
          <div key={title as string}>
            <p className={LABEL}>{title as string}</p>
            <ul className="mt-2.5 flex flex-col gap-2.5">
              {(rows as typeof SPEND_KEYS).map((r, i) => (
                <li key={r.label} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1">
                  <span className={cn(MONO, "truncate text-foreground")}>{r.label}</span>
                  <span className="text-right font-sans text-xs tabular-nums text-muted">Rs {fmt(r.value, 2)}</span>
                  <span className="col-span-2">
                    <Fill pct={r.pct} go={go} delay={0.12 * i + gi * 0.2} strong={i === 0} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-auto border-t border-border pt-4 font-sans text-xs text-muted">
        Every request&rsquo;s exact rupee cost comes back in its debug metrics.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------- 04 bench */

function BenchArt({ go, reduced }: ArtProps) {
  return (
    <div className={cn(PANEL, "gap-4")} aria-hidden="true">
      <div className="grid grid-cols-2 gap-3">
        {BENCH.tiles.map((t, i) => (
          <motion.div
            key={t.label}
            className="rounded-lg border border-border bg-background p-4"
            initial={{ opacity: 0, y: 10 }}
            animate={go ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.6, ease, delay: 0.1 * i }}
          >
            <p className="font-serif text-[1.9rem] font-light leading-none tracking-tight text-foreground sm:text-[2.2rem]">
              <Count to={t.value} go={go} decimals={t.decimals ?? 0} prefix={t.prefix ?? ""} suffix={t.suffix ?? ""} reduced={reduced} />
              <span className="ml-1.5 font-sans text-sm text-muted">{t.unit}</span>
            </p>
            <p className={cn(LABEL, "mt-2.5")}>{t.label}</p>
          </motion.div>
        ))}
      </div>

      {[BENCH.overhead, BENCH.throughput].map((chart, ci) => (
        <div key={chart.title} className="rounded-lg border border-border bg-background p-4">
          <p className="font-sans text-xs font-medium text-foreground">{chart.title}</p>
          <ul className="mt-3 flex flex-col gap-2.5">
            {chart.rows.map((r, i) => (
              <li key={r.label} className="grid grid-cols-[4.5rem_1fr_4.5rem] items-center gap-3">
                <span className={cn(MONO, "text-foreground")}>{r.label}</span>
                <Fill pct={(r.value / chart.max) * 100} go={go} delay={0.3 + ci * 0.3 + i * 0.12} strong={"strong" in r && r.strong === true} />
                <span className="text-right font-sans text-xs tabular-nums text-muted">
                  {fmt(r.value, chart.decimals)} {chart.unit}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 font-sans text-[0.68rem] leading-relaxed text-muted-2">{chart.note}</p>
        </div>
      ))}
    </div>
  );
}

const ART: Record<Chapter["art"], (p: ArtProps) => React.ReactElement> = {
  keys: KeysArt,
  policy: PolicyArt,
  spend: SpendArt,
  bench: BenchArt,
};

/* ------------------------------------------------------------------ view */

function Check() {
  return (
    <span
      aria-hidden="true"
      className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border border-border-strong text-foreground"
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </span>
  );
}

function ChapterView({ c }: { c: Chapter }) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduced = useReducedMotion() ?? false;
  const Art = ART[c.art];

  return (
    <motion.article
      ref={ref}
      variants={stagger(0.1)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-stretch lg:gap-14 xl:gap-20"
    >
      {/* text: vertically centred against the panel, with the rail node
          pinned to the heading's top */}
      <motion.div variants={fadeUp} className="relative lg:self-center">
        <div aria-hidden="true" className="absolute -left-16 top-1 hidden items-center gap-3 lg:flex">
          <span className="font-mono text-[0.68rem] tabular-nums text-muted-2">{c.n}</span>
          <span className="grid h-3.5 w-3.5 -translate-x-[calc(50%+0.2rem)] place-items-center rounded-full border border-foreground bg-background">
            <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
          </span>
        </div>
        <span className="font-mono text-xs tabular-nums text-muted-2 lg:hidden">{c.n}</span>
        <h3 className="mt-2 font-serif text-[clamp(1.5rem,2.7vw,2.25rem)] font-light leading-[1.18] tracking-[-0.01em] lg:mt-0">
          <span className="font-medium">{c.lead}</span> {c.rest}
        </h3>
        <p className="mt-5 max-w-xl font-sans text-sm leading-relaxed text-muted sm:text-[0.95rem]">{c.body}</p>
        <ul className="mt-7 flex flex-col gap-3.5">
          {c.points.map((p) => (
            <li key={p.strong} className="flex items-start gap-3 font-sans text-sm leading-relaxed text-foreground/85">
              <Check />
              <span>
                <span className="font-medium text-foreground">{p.strong}</span> {p.rest}
                {p.href && (
                  <>
                    {" "}
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground underline decoration-border-strong underline-offset-4 transition-colors hover:decoration-foreground"
                    >
                      {p.linkLabel ?? "Read the docs"}
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div variants={fadeUp} className="min-w-0">
        <Art go={inView} reduced={reduced} />
      </motion.div>
    </motion.article>
  );
}

export function GatewayFeatures() {
  return (
    <section id="gateway" className="relative scroll-mt-24">
      <div className="mx-auto max-w-[90rem] px-5 py-16 sm:px-8 sm:py-32">
        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
          className="max-w-4xl"
        >
          <motion.p variants={fadeUp} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-serif text-[1.35rem] italic leading-none text-foreground">&ldquo;P/9 Gateway&rdquo;</span>
            <span className="eyebrow">Sovereign gateway features</span>
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mt-5 font-serif text-[clamp(1.6rem,3.4vw,2.75rem)] font-light leading-[1.15] tracking-[-0.015em]"
          >
            Give your whole team access to{" "}
            <span className="font-normal">every model</span>, then see, control and cap{" "}
            <span className="font-normal">every request</span>.
          </motion.h2>
        </motion.div>

        {/* The chapters hang off a vertical rail with a numbered node each,
            like a table of contents you scroll down. The rail is decorative
            and hidden below `lg`, where the number sits inline instead. */}
        <div className="relative mt-16 sm:mt-24">
          <div aria-hidden="true" className="absolute left-0 top-0 bottom-0 hidden w-px bg-border lg:block" />
          <div className="flex flex-col gap-20 sm:gap-28 lg:gap-32 lg:pl-16">
            {CHAPTERS.map((c) => (
              <ChapterView key={c.n} c={c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
